import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import KYCDocumentCollection from '../components/KYCDocumentCollection';
import { UserMetadataService } from '../services/userMetadataService';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showKYCForm, setShowKYCForm] = useState(false);
  const [showApprovalMessage, setShowApprovalMessage] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<{
    isStrong: boolean;
    score: number;
    feedback: string[];
  } | null>(null);

  const { register, signInWithGoogle, signInWithFacebook } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Check password strength when password field changes
    if (e.target.name === 'password') {
      const strength = UserMetadataService.validatePasswordStrength(e.target.value);
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    // Check password strength
    if (passwordStrength && !passwordStrength.isStrong) {
      toast.error('Your password should be strong.');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setIsLoading(true);

    try {
      const user = await register(formData.name, formData.email, formData.password);
      toast.success('Registration successful! Please complete your KYC verification.');
      setRegisteredUserId(user.uid);
      setShowKYCForm(true);
    } catch (error: any) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: string, signInMethod: () => Promise<any>) => {
    setSocialLoading(provider);
    try {
      const userData = await signInMethod();
      
      // For social sign-in, users will need to complete KYC
      if (userData.status === 'pending') {
        toast.success(`Successfully registered with ${provider}! Please complete your KYC verification.`);
        setRegisteredUserId(userData.uid);
        setShowKYCForm(true);
      } else if (userData.status === 'approved') {
        toast.success(`Successfully signed in with ${provider}!`);
        navigate('/dashboard');
      } else {
        toast.error('Account status unknown. Please contact support.');
      }
    } catch (error: any) {
      toast.error(error.message || `Failed to sign in with ${provider}`);
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="text-center">
            <div className="flex justify-center mb-3">
              <Logo size="xxxl" />
            </div>
            <h2 className="auth-title">
              Let's respect the market
            </h2>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium theme-text-secondary"
              >
                {/* Full Name */}
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium theme-text-secondary"
              >
                {/* Email address */}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium theme-text-secondary"
              >
                {/* Password */}
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field pr-10"
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 theme-text-quaternary" />
                  ) : (
                    <Eye className="h-5 w-5 theme-text-quaternary" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs theme-text-quaternary">
                Must be at least 8 characters long
              </p>
              
              {/* Password Strength Indicator */}
              {formData.password && passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs theme-text-quaternary">Password strength:</span>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className={`h-2 w-8 rounded-full ${
                            level <= passwordStrength.score
                              ? passwordStrength.score >= 4
                                ? 'bg-[#2AB7A6]'
                                : passwordStrength.score >= 3
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                              : 'theme-bg-quaternary'
                          }`}
                        />
                      ))}
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score >= 4
                        ? 'text-[#2AB7A6]'
                        : passwordStrength.score >= 3
                        ? 'text-yellow-400'
                        : 'text-red-400'
                    }`}>
                      {passwordStrength.score >= 4 ? 'Strong' : passwordStrength.score >= 3 ? 'Medium' : 'Weak'}
                    </span>
                  </div>
                  
                  {passwordStrength.feedback.length > 0 && (
                    <div className="text-xs theme-text-quaternary">
                      {passwordStrength.feedback.map((feedback, index) => (
                        <div key={index} className="flex items-center space-x-1">
                          <AlertTriangle className="h-3 w-3 text-orange-400" />
                          <span>{feedback}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium theme-text-secondary"
              >
                {/* Confirm Password */}
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-field pr-10"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 theme-text-quaternary" />
                  ) : (
                    <Eye className="h-5 w-5 theme-text-quaternary" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-start space-x-3">
                              <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={e => setAgreedToTerms(e.target.checked)}
                  className="h-4 w-4 text-[#2AB7A6] focus:ring-[#2AB7A6] theme-border rounded theme-bg-quaternary mt-1"
                />
                <label htmlFor="terms" className="text-sm theme-text-secondary">
                I agree to the{' '}
                <Link
                  to="/terms-and-conditions"
                  className="text-[#2AB7A6] hover:text-[#25A395]"
                >
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy-policy"
                  className="text-[#2AB7A6] hover:text-[#25A395]"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#2AB7A6] hover:bg-[#25A395] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2AB7A6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>

                     <div className="mt-6">
             <div className="auth-divider">
               <div className="auth-divider-line" />
               <div className="auth-divider-text">
                 <span className="px-2">
                   Or continue with
                 </span>
               </div>
             </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
                             <button 
                 onClick={() => handleSocialSignIn('Google', signInWithGoogle)}
                 disabled={socialLoading !== null}
                 className="social-button"
               >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="ml-1 text-xs">
                  {socialLoading === 'Google' ? '...' : 'Google'}
                </span>
              </button>

                             <button 
                 onClick={() => handleSocialSignIn('Facebook', signInWithFacebook)}
                 disabled={socialLoading !== null}
                 className="social-button"
               >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="ml-1 text-xs">
                  {socialLoading === 'Facebook' ? '...' : 'Facebook'}
                </span>
              </button>
            </div>
                         <p className="auth-subtitle mt-6 text-center">
               Already have an account?{' '}
               <Link
                 to="/login"
                 className="font-medium text-[#2AB7A6] hover:text-[#25A395]"
               >
                 Sign in
               </Link>
             </p>
          </div>
        </div>
      </div>
      
      {/* KYC Document Collection Modal */}
      {showKYCForm && registeredUserId && (
        <KYCDocumentCollection
          isOpen={showKYCForm}
          onClose={() => setShowKYCForm(false)}
          userId={registeredUserId}
          onComplete={() => {
            setShowKYCForm(false);
            setShowApprovalMessage(true);
          }}
        />
      )}

      {/* Approval Message Modal */}
      {showApprovalMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="theme-bg-secondary rounded-lg p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 bg-opacity-10 mb-4">
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-lg font-semibold theme-text-primary mb-2">
                Account Under Review
              </h3>
              <p className="theme-text-secondary text-sm leading-relaxed">
                Thank you for completing your KYC verification! Your account is now pending admin approval. 
                Our team will review your documents and you'll receive an email 
                once your account is approved (usually within 24 hours).
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowApprovalMessage(false);
                  navigate('/login');
                }}
                className="w-full px-4 py-2 bg-[#2AB7A6] hover:bg-[#25A395] text-white rounded-lg transition-colors duration-200"
              >
                Go to Login
              </button>
              <button
                onClick={() => setShowApprovalMessage(false)}
                className="w-full px-4 py-2 theme-bg-quaternary hover:theme-bg-quinary text-white rounded-lg transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
